/* ============================================================================
 * Movatic WMS — Frontend | MES » OrdersPage | CesarFilho 932048016
 * ========================================================================== */

import React, { useEffect, useMemo, useState } from "react";
import MES, { ProdOrder } from "@/api/mes";

type Loading = "idle" | "list" | "action";

const STATUS_LABEL: Record<string, string> = {
    DRAFT: "Draft",
    PLANNED: "Planned",
    ALLOCATED: "Allocated",
    PICKED: "Picked",
    IN_PRODUCTION: "In production",
    PAUSED: "Paused",
    DONE: "Done",
    PUTAWAY: "Putaway",
    CLOSED: "Closed",
    CANCELED: "Canceled",
};

export default function OrdersPage() {
    const [rows, setRows] = useState<ProdOrder[]>([]);
    const [loading, setLoading] = useState<Loading>("idle");
    const [q, setQ] = useState("");
    const [status, setStatus] = useState<string>("");

    const filtered = useMemo(() => {
        const list = Array.isArray(rows) ? rows : [];
        const byQ = (r: ProdOrder) =>
            !q ||
            r.ref_code?.toLowerCase().includes(q.toLowerCase()) ||
            (r.product_code || "").toLowerCase().includes(q.toLowerCase());
        const byS = (r: ProdOrder) => !status || r.status === status;
        return list.filter((r) => byQ(r) && byS(r));
    }, [rows, q, status]);

    async function refresh() {
        setLoading("list");
        try {
            const data = await MES.listOrders();
            setRows(Array.isArray(data) ? data : []);
        } catch (e: any) {
            alert("Erro ao listar ordens: " + e.message);
            setRows([]);
        } finally {
            setLoading("idle");
        }
    }

    useEffect(() => { refresh(); }, []);

    async function action(id: number, kind: string) {
        setLoading("action");
        try {
            if (kind === "allocate") await MES.allocate(id);
            else if (kind === "start") await MES.start(id);
            else if (kind === "pause") await MES.pause(id);
            else if (kind === "resume") await MES.resume(id);
            else if (kind === "finish") await MES.finish(id);
            else if (kind === "consume") {
                const component = Number(prompt("ID do componente?") || "0");
                const qty = Number(prompt("Quantidade?") || "0");
                const lot = prompt("Lote (opcional)?") || "";
                if (!component || !qty) return;
                await MES.consume(id, { component, qty, lot });
            } else if (kind === "report") {
                const qty = Number(prompt("Qtd produzida?") || "0");
                const lot = prompt("Lote do acabado (opcional)?") || "";
                const sscc = prompt("SSCC (opcional)?") || "";
                if (!qty) return;
                await MES.report(id, { qty, lot, sscc });
            }
            await refresh();
        } catch (e: any) {
            alert(`Falha na ação ${kind}: ` + e.message);
        } finally {
            setLoading("idle");
        }
    }

    return (
        <div className="p-4 space-y-4">
            <div className="flex items-center gap-2">
                <h1 className="text-xl font-semibold">MES — Ordens de Produção</h1>
                <button className="px-3 py-1 rounded bg-gray-800 hover:bg-gray-700" onClick={refresh} disabled={loading !== "idle"}>
                    Recarregar
                </button>
                <div className="ml-auto flex gap-2">
                    <input
                        className="px-2 py-1 rounded bg-gray-900 border border-gray-700"
                        placeholder="Pesquisar (ref/produto)"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                    />
                    <select
                        className="px-2 py-1 rounded bg-gray-900 border border-gray-700"
                        value={status}
                        onChange={(e) => setStatus(e.target.value)}
                    >
                        <option value="">Todos</option>
                        {Object.keys(STATUS_LABEL).map((s) => (
                            <option key={s} value={s}>{STATUS_LABEL[s]}</option>
                        ))}
                    </select>
                </div>
            </div>

            <div className="overflow-auto rounded border border-gray-800">
                <table className="min-w-full text-sm">
                    <thead className="bg-gray-900">
                        <tr>
                            <th className="p-2 text-left">Ref</th>
                            <th className="p-2 text-left">Produto</th>
                            <th className="p-2 text-left">Qtd</th>
                            <th className="p-2 text-left">UOM</th>
                            <th className="p-2 text-left">WC</th>
                            <th className="p-2 text-left">Estado</th>
                            <th className="p-2 text-left">Ações</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.map((r) => (
                            <tr key={r.id} className="border-t border-gray-800">
                                <td className="p-2">{r.ref_code}</td>
                                <td className="p-2">{r.product_code ?? r.product}</td>
                                <td className="p-2">{r.qty}</td>
                                <td className="p-2">{r.uom}</td>
                                <td className="p-2">{r.workcenter ?? "-"}</td>
                                <td className="p-2">
                                    <span className="px-2 py-0.5 rounded bg-gray-800">
                                        {STATUS_LABEL[r.status] || r.status}
                                    </span>
                                </td>
                                <td className="p-2 space-x-1">
                                    <button className="px-2 py-0.5 bg-gray-800 rounded" onClick={() => action(r.id, "allocate")}>Allocate</button>
                                    <button className="px-2 py-0.5 bg-gray-800 rounded" onClick={() => action(r.id, "start")}>Start</button>
                                    <button className="px-2 py-0.5 bg-gray-800 rounded" onClick={() => action(r.id, "pause")}>Pause</button>
                                    <button className="px-2 py-0.5 bg-gray-800 rounded" onClick={() => action(r.id, "resume")}>Resume</button>
                                    <button className="px-2 py-0.5 bg-gray-800 rounded" onClick={() => action(r.id, "consume")}>Consume</button>
                                    <button className="px-2 py-0.5 bg-gray-800 rounded" onClick={() => action(r.id, "report")}>Report</button>
                                    <button className="px-2 py-0.5 bg-gray-800 rounded" onClick={() => action(r.id, "finish")}>Finish</button>
                                </td>
                            </tr>
                        ))}
                        {filtered.length === 0 && (
                            <tr>
                                <td className="p-6 text-center text-gray-400" colSpan={7}>
                                    Sem ordens.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

